/**
 * 🪵 **Drawing the driftwood — a heap of loose pieces, not a cube.**
 *
 * **D105.** 🎯 **haugur, 2026-09-02, shown the alternative:** *"4 or 10 cubes in
 * a row on a beach is just funny looking."*
 *
 * ## 🔴 Why this is not in the world mesh
 *
 * `mesher.ts` emits **axis-aligned faces of a box scaled in Y**, and that is its
 * whole vocabulary. A driftwood cell is **two to five separate pieces, each
 * bowed, tapered and lying at its own angle**, which cannot be said in that
 * language at all. So the mesher skips it and this draws it, exactly as
 * `torchmesh.ts` and `flame.ts` already are.
 *
 * ⚠️ **It still occupies its cell in the chunk array.** Skipping it in the mesh
 * hides it from the MESH, not from the world — the crosshair, reach and picking
 * it up all go on seeing it.
 *
 * ## ⚠️ The shape comes from `driftgeom.ts` and is NOT re-implemented here
 *
 * That module is pure arithmetic with no `three` import, so it tests without a
 * browser or a GPU. **This file is the wrapper that puts it in the scene** —
 * the same split the project already uses everywhere.
 *
 * ## 🔴 Pieces OVERHANG their cell, and the margin is what makes that safe
 *
 * 🎯 **haugur:** *"yes let them overhang the cell."* A piece may reach
 * `SHAPE.maxOverhang` outside its own metre, so **a chunk must draw the
 * driftwood in a margin around itself as well as inside it** — otherwise a piece
 * rooted one cell outside would be clipped away at the boundary and pop into
 * existence as you walked. **Birch already does this**, and for the same reason:
 * a crown overhangs its neighbours.
 *
 * ⚠️ **Not done yet, and said out loud rather than left to be discovered:** the
 * caller passes one chunk's blocks, so a piece rooted in the NEXT chunk is
 * missing from this one's mesh. At `maxOverhang` 0.6 m that is a **0.6 m sliver
 * at a 32 m boundary** — visible if you look for it, and the fix is the margin
 * scan the trees already do.
 */

import { BufferAttribute, BufferGeometry, Color, Mesh, type Material } from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { uniform } from 'three/tsl';
import { CHUNK_SIZE, isDriftwood } from '@lofi/shared';
import { buildDriftPiece, driftPiecesAt, type DriftAt } from './driftgeom.ts';

/** Where a heap sits, in chunk-local cells. */
export interface DriftAtCell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** The WORLD position, which is what the shape is hashed from. */
  readonly world: DriftAt;
}

/**
 * Every driftwood cell in this chunk.
 *
 * 🔴 **`origin` is the chunk's world corner**, and it is needed because the
 * shape of a heap is hashed from **where it is in the world**, not from where it
 * is in the chunk. Hashing the local position would make every chunk's heaps
 * identical — the same fault as a texture that repeats.
 */
export function driftwoodIn(
  blocks: Uint16Array,
  origin: { readonly x: number; readonly y: number; readonly z: number },
): DriftAtCell[] {
  const out: DriftAtCell[] = [];
  for (let i = 0; i < blocks.length; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array. Same reasoning as torchmesh.ts. */
    const id = blocks[i] ?? 0;
    if (!isDriftwood(id)) continue;
    const x = i % CHUNK_SIZE;
    const y = Math.floor(i / (CHUNK_SIZE * CHUNK_SIZE));
    const z = Math.floor(i / CHUNK_SIZE) % CHUNK_SIZE;
    out.push({
      x,
      y,
      z,
      world: { x: origin.x + x, y: origin.y + y, z: origin.z + z },
    });
  }
  return out;
}

/**
 * Build one geometry for every heap in the chunk, or `null` if there are none.
 *
 * ⚠️ **One buffer for the whole chunk**, not one per heap: a draw call per piece
 * would be several hundred on a busy shore, and they all wear the same material.
 *
 * 🔴 **Positions are CHUNK-LOCAL.** `driftgeom` returns cell-local metres —
 * because a 32-bit float at the world box's real easting is spaced about 3 cm
 * apart, which is wider than a driftwood piece's features — so the cell offset
 * is added here and the chunk's own offset is applied to the mesh.
 */
export function buildDriftGeometry(heaps: readonly DriftAtCell[]): BufferGeometry | null {
  if (heaps.length === 0) return null;

  const parts: { positions: Float32Array; normals: Float32Array; indices: Uint32Array }[] = [];
  const at: DriftAtCell[] = [];
  for (const heap of heaps) {
    for (let i = 0; i < driftPiecesAt(heap.world); i++) {
      parts.push(buildDriftPiece(heap.world, i));
      at.push(heap);
    }
  }

  const verts = parts.reduce((n, p) => n + p.positions.length / 3, 0);
  const tris = parts.reduce((n, p) => n + p.indices.length, 0);
  const pos = new Float32Array(verts * 3);
  const nrm = new Float32Array(verts * 3);
  const idx = new Uint32Array(tris);

  let v = 0;
  let t = 0;
  for (const [n, part] of parts.entries()) {
    const cell = at.at(n);
    if (cell === undefined) continue;
    const base = v / 3;
    /* ⚠️ `map` and `set`, never an indexed write — the same object-injection
       rule `driftgeom.ts` and `blocks.ts` follow, and `set` copies a whole run
       in one call rather than a element at a time. */
    const offset = [cell.x, cell.y, cell.z];
    pos.set(
      part.positions.map((value, i) => value + (offset.at(i % 3) ?? 0)),
      v,
    );
    nrm.set(part.normals, v);
    idx.set(
      part.indices.map((i) => base + i),
      t,
    );
    v += part.positions.length;
    t += part.indices.length;
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(pos, 3));
  geometry.setAttribute('normal', new BufferAttribute(nrm, 3));
  geometry.setIndex(new BufferAttribute(idx, 1));
  return geometry;
}

/**
 * The mesh for one chunk's driftwood, or `null`.
 *
 * ⚠️ **The material is passed in and is one the running game already
 * compiles.** 🔴 That is `06_Traps.md`'s hardest-won rule: *"prefer a material
 * that is already in use"* — a smoke shader stopped the world on 2026-08-31 and
 * a texture on the held item blacked out dev **and** staging on 2026-09-01, and
 * **a shader cannot be checked on this workstation at all.**
 */
export function buildDriftMesh(heaps: readonly DriftAtCell[], material: Material): Mesh | null {
  const geometry = buildDriftGeometry(heaps);
  return geometry === null ? null : new Mesh(geometry, material);
}

/**
 * 🎨 **The material, and it is deliberately the DULLEST option available.**
 *
 * 🔴 **`MeshLambertNodeMaterial` with a `colorNode`, which is exactly what the
 * torch already compiles** — `06_Traps.md`'s hardest rule: *"PREFER A MATERIAL
 * THAT IS ALREADY IN USE."* A smoke shader stopped the world on 2026-08-31 and a
 * TEXTURE on the held item blacked out dev **and** staging on 2026-09-01, and
 * **a shader cannot be checked on this workstation at all** — `navigator.gpu` is
 * missing under every flag tried.
 *
 * ⚠️ **SO THE DRIFTWOOD IS FLAT-COLOURED, NOT TEXTURED, AND THAT IS A REAL
 * LIMITATION RATHER THAN A CHOICE ABOUT LOOKS.** The picture haugur chose out of
 * four IS packed and IS on the block — it is what the pack icon and any future
 * cube face use — but putting it on these triangles needs UV coordinates this
 * geometry does not generate and a material permutation nothing here has ever
 * proved safe. 👉 **Colour first, texture as its own change, deployed to dev by
 * hand and looked at**, the way the hand is going to have to be.
 *
 * ✅ The colour is the mean of that same texture, so the two cannot drift apart.
 */
export function buildDriftMaterial(colour: string): MeshLambertNodeMaterial {
  const material = new MeshLambertNodeMaterial();
  /* ⚠️ A NodeMaterial ignores `material.color` — a white default reads as a
     lighting bug, which is a trap this project has already paid for. */
  material.colorNode = uniform(new Color(colour));
  return material;
}
